import { useEffect, useState } from 'react';
import { generateQRCode } from '@playcastdotio/Qrcode';

interface UseThankYouQrCodeOptions {
  /** The exact outbound URL the "Download on [Store]" button opens. */
  ctaUrl: string | undefined;
}

/**
 * Renders a QR encoding the CTA URL, regenerated whenever the URL changes.
 *
 * The outbound URL carries per-visitor UTMs and pass-through params resolved at
 * theme load, so the code is regenerated on every fresh resolution (every page
 * load in practice) rather than cached — a code generated for one visitor's URL
 * must never be handed to another.
 *
 * Same engine as the handoff QR (`@playcastdotio/Qrcode`), but without the
 * refresh interval: the handoff code encodes a short-lived bridge token that
 * expires on screen, while this one encodes a stable outbound link that is
 * valid for as long as the page shows it.
 */
export function useThankYouQrCode({ ctaUrl }: UseThankYouQrCodeOptions): { qrDataUrl: string | null } {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!ctaUrl) {
      setQrDataUrl(null);
      return;
    }

    let cancelled = false;
    void generateQRCode(ctaUrl)
      .then((dataUrl) => {
        if (!cancelled) setQrDataUrl(dataUrl ?? null);
      })
      .catch((error) => {
        console.warn('[QuickstartThankYou] Failed to generate QR code:', error);
        if (!cancelled) setQrDataUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [ctaUrl]);

  return { qrDataUrl };
}
